import axios from 'axios'

import { type GalleryItem as BackofficeGalleryItem } from '@/pages/gallery/gallery.type'
import {
  type MenuChoice,
  type NavBarContent,
  parseServerMenuElement,
  serializeMenuElement,
  type ServerDeeplinkElement,
} from '@/pages/menuEntries/MenuEntry.type'

type ServerNavBarContent = {
  _id?: string
  type: 'BOTTOM_NAV'
  fontSize?: number
  elements: ServerDeeplinkElement[]
}

type ServerMenuChoice = ServerDeeplinkElement & {
  choices: MenuChoice['choices']
  multiselect: boolean
  removable?: boolean
}

function parseNavBarContent(
  s: ServerNavBarContent,
  galleryRecord: Record<string, BackofficeGalleryItem>,
): NavBarContent {
  return {
    _id: s._id,
    type: 'BOTTOM_NAV',
    fontSize: s.fontSize,
    elements: s.elements.map((e) => parseServerMenuElement(e, galleryRecord)),
  }
}

function serializeNavBarContent(n: NavBarContent): ServerNavBarContent {
  return {
    _id: n._id,
    type: 'BOTTOM_NAV',
    fontSize: n.fontSize,
    elements: n.elements.map(serializeMenuElement),
  }
}

function parseServerMenuChoice(
  s: ServerMenuChoice,
  galleryRecord: Record<string, BackofficeGalleryItem>,
): MenuChoice {
  return {
    ...parseServerMenuElement(s, galleryRecord),
    choices: s.choices,
    multiselect: s.multiselect,
  }
}

export const NavbarContentService = {
  async getNavbarContent(
    galleryRecord: Record<string, BackofficeGalleryItem>,
  ): Promise<NavBarContent> {
    const { data } = await axios.get<ServerNavBarContent>('/api/navbarContent')
    return parseNavBarContent(data, galleryRecord)
  },

  // Returns void: no caller uses the response. Refetch via
  // `getNavbarContent` if needed (avoids threading galleryRecord through).
  async save(menuContent: NavBarContent): Promise<void> {
    await axios.post<ServerNavBarContent>(
      '/api/navbarContent',
      serializeNavBarContent(menuContent),
    )
  },

  async getChoices(
    galleryRecord: Record<string, BackofficeGalleryItem>,
  ): Promise<MenuChoice[]> {
    const { data } = await axios.get<ServerMenuChoice[]>(
      '/api/navbarContent/choices',
    )
    return data.map((c) => parseServerMenuChoice(c, galleryRecord))
  },

  async getAllSecrets(): Promise<string[]> {
    const { data } = await axios.get('/api/navbarContent/secrets')
    return data
  },

  async removeSecret(secret: string): Promise<void> {
    await axios.delete(`/api/navbarContent/secrets/${secret}`)
  },
}
